/**
 * Utilitário centralizado para formatação de datas com timezone
 * 
 * ============================================================================
 * SISTEMA INTERNACIONAL - CONFIGURAÇÃO DE TIMEZONE POR EMPRESA
 * ============================================================================
 * 
 * Este utilitário suporta timezone dinâmico por empresa.
 * 
 * COMO FUNCIONA:
 * 1. Quando o usuário faz login, o backend retorna o timezone da empresa
 * 2. O timezone é salvo no localStorage como "companyTimezone"
 * 3. getTimezone() busca primeiro do localStorage, depois usa o fallback
 * 
 * CONFIGURAÇÃO:
 * O timezone da empresa é configurado em:
 * - Configurações > Sistema > Fuso Horário
 * 
 * PROBLEMA RESOLVIDO:
 * O date-fns padrão usa o timezone do sistema operacional/browser.
 * Se o browser estiver em UTC mas o usuário estiver no Brasil,
 * as datas são exibidas incorretamente (ex: mensagem de 22:58 do dia 19
 * aparece como dia 20 se o browser usar UTC).
 * 
 * SOLUÇÃO:
 * Usar date-fns-tz para forçar o timezone configurado pela empresa em todas
 * as formatações de data do sistema.
 * 
 * DEFAULT_TIMEZONE: "America/Sao_Paulo" (Brasil) - usado quando não há
 * timezone configurado no localStorage
 * 
 * EXEMPLOS DE TIMEZONES IANA VÁLIDOS:
 * - "America/New_York" (EUA - Eastern)
 * - "America/Los_Angeles" (EUA - Pacific)
 * - "Europe/London" (Reino Unido)
 * - "Europe/Paris" (França)
 * - "Europe/Madrid" (Espanha)
 * - "Asia/Tokyo" (Japão)
 * - "Asia/Singapore" (Singapura)
 * - "Australia/Sydney" (Austrália)
 * - "America/Mexico_City" (México)
 * - "America/Argentina/Buenos_Aires" (Argentina)
 * 
 * @see https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
 * @author AI Assistant
 * @date 2025-12-20
 */

import { parseISO as dateFnsParseISO, isValid as isValidDate } from "date-fns";
import { formatInTimeZone, utcToZonedTime } from "date-fns-tz";
import { ptBR } from "date-fns/locale";

// ============================================================================
// CONFIGURAÇÃO DO TIMEZONE
// ============================================================================

/**
 * Timezone padrão do sistema
 * 
 * IMPORTANTE: Este valor deve corresponder ao timezone configurado no backend
 * (backend/src/config/database.ts -> timezone: "America/Sao_Paulo")
 */
const DEFAULT_TIMEZONE = "America/Sao_Paulo";

/**
 * Cache do timezone para evitar múltiplas leituras do localStorage
 * 
 * PERFORMANCE: Em uma lista de 100 mensagens, sem cache teríamos 300+
 * leituras do localStorage. Com cache, temos apenas 1.
 */
let cachedTimezone = null;
let cacheTimestamp = 0;
const CACHE_TTL_MS = 5000; // 5 segundos - permite atualização dinâmica

/**
 * Validação interna de timezone (sem loop de dependência)
 * @param {string} timezone - Timezone a validar
 * @returns {boolean} - true se for válido
 */
const isValidTimezoneInternal = (timezone) => {
  if (!timezone || typeof timezone !== "string") return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Retorna o timezone atual da empresa
 * 
 * Ordem de prioridade:
 * 1. Cache válido (< 5 segundos)
 * 2. Timezone da empresa no localStorage (configurado pelo admin)
 * 3. Fallback para o timezone padrão (Brasil)
 * 
 * PERFORMANCE: Usa cache para evitar múltiplas leituras do localStorage
 * em renderizações frequentes (ex: lista de mensagens).
 * 
 * @returns {string} - Timezone IANA (ex: "America/Sao_Paulo")
 */
export const getTimezone = () => {
  const now = Date.now();
  
  // 1. Verificar cache válido
  if (cachedTimezone && (now - cacheTimestamp) < CACHE_TTL_MS) {
    return cachedTimezone;
  }
  
  // 2. Ler do localStorage
  try {
    const companyTimezone = localStorage.getItem("companyTimezone");
    if (companyTimezone && isValidTimezoneInternal(companyTimezone)) {
      cachedTimezone = companyTimezone;
      cacheTimestamp = now;
      return cachedTimezone;
    }
  } catch (error) {
    // localStorage pode não estar disponível em alguns contextos (SSR, workers)
    console.warn("[dateUtils] localStorage não disponível:", error);
  }
  
  // 3. Fallback para o timezone padrão
  cachedTimezone = DEFAULT_TIMEZONE;
  cacheTimestamp = now;
  return cachedTimezone;
};

/**
 * Força atualização do cache de timezone
 * Deve ser chamado quando o timezone é alterado nas configurações
 */
export const invalidateTimezoneCache = () => {
  cachedTimezone = null;
  cacheTimestamp = 0;
};

/**
 * Alias para compatibilidade e clareza
 * @deprecated Use getTimezone() para código novo
 */
export const SYSTEM_TIMEZONE = DEFAULT_TIMEZONE;

// ============================================================================
// FUNÇÕES DE PARSING
// ============================================================================

/**
 * Faz parse de uma string ISO e retorna um Date object
 * 
 * EDGE CASES TRATADOS:
 * - null/undefined → retorna new Date()
 * - string vazia → retorna new Date()
 * - Date object válido → retorna o próprio objeto
 * - Date object inválido (Invalid Date) → retorna new Date()
 * - string inválida → retorna new Date() com warning no console
 * - número (timestamp) → converte para Date
 * 
 * @param {string|Date|number} dateString - String ISO, Date object ou timestamp
 * @returns {Date} - Date object válido
 */
export const parseISO = (dateString) => {
  // Caso 1: null, undefined ou string vazia
  if (!dateString) return new Date();
  
  // Caso 2: Já é um Date object
  if (dateString instanceof Date) {
    // Verificar se é um Date válido (não é "Invalid Date")
    return isValidDate(dateString) ? dateString : new Date();
  }
  
  // Caso 3: É um número (timestamp)
  if (typeof dateString === "number") {
    const date = new Date(dateString);
    return isValidDate(date) ? date : new Date();
  }
  
  // Caso 4: É uma string - tentar fazer parse
  if (typeof dateString === "string") {
    try {
      const parsed = dateFnsParseISO(dateString);
      if (isValidDate(parsed)) {
        return parsed;
      }
      // String não é ISO válida, tentar new Date()
      const fallback = new Date(dateString);
      if (isValidDate(fallback)) {
        return fallback;
      }
    } catch (error) {
      // Silenciar erro, tentar fallback
    }
  }
  
  // Caso 5: Tipo não suportado ou parse falhou
  console.warn("[dateUtils] Falha ao fazer parse de data, usando data atual:", dateString);
  return new Date();
};

// ============================================================================
// FUNÇÕES DE FORMATAÇÃO
// ============================================================================

/**
 * Formata uma data usando o timezone configurado
 * 
 * EDGE CASES TRATADOS:
 * - null/undefined → retorna ""
 * - Date inválido → retorna ""
 * - formatStr vazio → usa "dd/MM/yyyy HH:mm" como padrão
 * - timezone inválido → usa DEFAULT_TIMEZONE
 * 
 * @param {Date|string|number} date - Date object, string ISO ou timestamp
 * @param {string} formatStr - String de formato (ex: "dd/MM/yyyy", "HH:mm")
 * @param {Object} options - Opções adicionais
 * @param {string} options.timezone - Timezone específico (opcional, usa padrão se não informado)
 * @returns {string} - Data formatada ou "" em caso de erro
 */
export const formatWithTimezone = (date, formatStr, options = {}) => {
  // Validação de entrada
  if (date === null || date === undefined) return "";
  if (!formatStr) formatStr = "dd/MM/yyyy HH:mm";
  
  const timezone = options.timezone || getTimezone();
  
  try {
    // Converter para Date object se necessário
    const dateObj = parseISO(date);
    
    // Verificar se o Date é válido
    if (!isValidDate(dateObj)) {
      console.warn("[dateUtils] Data inválida:", date);
      return "";
    }
    
    return formatInTimeZone(dateObj, timezone, formatStr, { locale: ptBR });
  } catch (error) {
    console.warn("[dateUtils] Erro ao formatar data:", date, error);
    return "";
  }
};

/**
 * Formata uma data para exibição de horário (HH:mm)
 * 
 * @param {Date|string} date - Date object ou string ISO
 * @returns {string} - Horário formatado (ex: "08:19")
 */
export const formatTime = (date) => {
  return formatWithTimezone(date, "HH:mm");
};

/**
 * Formata uma data para exibição completa (dd/MM/yyyy)
 * 
 * @param {Date|string} date - Date object ou string ISO
 * @returns {string} - Data formatada (ex: "20/12/2025")
 */
export const formatDate = (date) => {
  return formatWithTimezone(date, "dd/MM/yyyy");
};

/**
 * Formata uma data para exibição completa com horário (dd/MM/yyyy HH:mm)
 * 
 * @param {Date|string} date - Date object ou string ISO
 * @returns {string} - Data e horário formatados (ex: "20/12/2025 08:19")
 */
export const formatDateTime = (date) => {
  return formatWithTimezone(date, "dd/MM/yyyy HH:mm");
};

// ============================================================================
// FUNÇÕES DE COMPARAÇÃO
// ============================================================================

/**
 * Verifica se duas datas são do mesmo dia NO TIMEZONE CONFIGURADO
 * 
 * IMPORTANTE: Esta função é crítica para o agrupamento de mensagens.
 * Sem o timezone correto, mensagens de 22:58 do dia 19 podem ser
 * agrupadas com mensagens de 08:19 do dia 20 (se o browser usar UTC).
 * 
 * EDGE CASES TRATADOS:
 * - null/undefined → retorna false
 * - Datas inválidas → retorna false
 * - Mesma referência de Date → retorna true (otimização)
 * 
 * @param {Date|string|number} date1 - Primeira data
 * @param {Date|string|number} date2 - Segunda data
 * @param {Object} options - Opções adicionais
 * @param {string} options.timezone - Timezone específico (opcional)
 * @returns {boolean} - true se são do mesmo dia no timezone configurado
 */
export const isSameDayInTimezone = (date1, date2, options = {}) => {
  // Validação de entrada
  if (date1 === null || date1 === undefined) return false;
  if (date2 === null || date2 === undefined) return false;
  
  // Otimização: mesma referência
  if (date1 === date2) return true;
  
  const timezone = options.timezone || getTimezone();
  
  try {
    const dateObj1 = parseISO(date1);
    const dateObj2 = parseISO(date2);
    
    // Verificar se ambas as datas são válidas
    if (!isValidDate(dateObj1) || !isValidDate(dateObj2)) {
      return false;
    }
    
    // Converter para o timezone configurado
    const zonedDate1 = utcToZonedTime(dateObj1, timezone);
    const zonedDate2 = utcToZonedTime(dateObj2, timezone);
    
    // Comparar apenas a data (ignorando horário)
    return (
      zonedDate1.getFullYear() === zonedDate2.getFullYear() &&
      zonedDate1.getMonth() === zonedDate2.getMonth() &&
      zonedDate1.getDate() === zonedDate2.getDate()
    );
  } catch (error) {
    console.warn("[dateUtils] Erro ao comparar datas:", date1, date2, error);
    return false;
  }
};

/**
 * Retorna a data de hoje formatada no timezone configurado
 * 
 * @param {string} formatStr - String de formato (default: "dd/MM/yyyy")
 * @returns {string} - Data de hoje formatada
 */
export const getTodayFormatted = (formatStr = "dd/MM/yyyy") => {
  return formatWithTimezone(new Date(), formatStr);
};

/**
 * Verifica se uma data é hoje no timezone configurado
 * 
 * @param {Date|string} date - Data a verificar
 * @returns {boolean} - true se for hoje
 */
export const isToday = (date) => {
  return isSameDayInTimezone(date, new Date());
};

// ============================================================================
// FUNÇÕES UTILITÁRIAS ADICIONAIS
// ============================================================================

/**
 * Retorna informações sobre o timezone atual
 * Útil para debugging e exibição em configurações
 * 
 * @returns {Object} - Informações do timezone
 */
export const getTimezoneInfo = () => {
  const timezone = getTimezone();
  const now = new Date();
  
  return {
    timezone,
    currentTime: formatWithTimezone(now, "dd/MM/yyyy HH:mm:ss"),
    utcOffset: formatWithTimezone(now, "xxx"), // ex: "-03:00"
    isDefault: timezone === DEFAULT_TIMEZONE,
  };
};

/**
 * Valida se um timezone IANA é válido
 * 
 * @param {string} timezone - Timezone a validar
 * @returns {boolean} - true se for válido
 */
export const isValidTimezone = (timezone) => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch (error) {
    return false;
  }
};

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

const dateUtils = {
  // Configuração
  getTimezone,
  getTimezoneInfo,
  isValidTimezone,
  invalidateTimezoneCache,
  SYSTEM_TIMEZONE,
  
  // Parsing
  parseISO,
  
  // Formatação
  formatWithTimezone,
  formatTime,
  formatDate,
  formatDateTime,
  
  // Comparação
  isSameDayInTimezone,
  getTodayFormatted,
  isToday,
};

export default dateUtils;
